/**
 * Approved email-domain helpers for User Management (MVP 1.2.1 / company domain).
 * Empty domain lists mean all email domains are accepted for that account.
 */

/** Normalize a single domain token (strip @, lowercase, trim). */
export function normalizeApprovedEmailDomain(raw: string): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/^@+/, '')
    .replace(/\.+$/, '');
}

/** Parse free-text (comma / whitespace / newline separated) into unique normalized domains. */
export function parseApprovedEmailDomains(raw: string | string[] | null | undefined): string[] {
  const chunks = Array.isArray(raw)
    ? raw
    : String(raw || '')
        .split(/[\s,;]+/)
        .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const chunk of chunks) {
    const domain = normalizeApprovedEmailDomain(chunk);
    if (!domain || !domain.includes('.') || seen.has(domain)) {
      continue;
    }
    // Reject tokens that look like full emails
    if (domain.includes('@')) {
      continue;
    }
    seen.add(domain);
    out.push(domain);
  }
  return out;
}

/** Extract the domain portion of an email (lowercase). */
export function emailDomainOf(email: string): string {
  const trimmed = String(email || '').trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at < 0 || at === trimmed.length - 1) {
    return '';
  }
  return normalizeApprovedEmailDomain(trimmed.slice(at + 1));
}

/**
 * Whether `email` is allowed for a single customer account.
 * Empty / missing approved list → all domains accepted.
 */
export function isEmailAllowedForApprovedDomains(
  email: string,
  approvedDomains: string[] | null | undefined
): boolean {
  const allowed = parseApprovedEmailDomains(approvedDomains || []);
  if (!allowed.length) {
    return true;
  }
  const domain = emailDomainOf(email);
  if (!domain) {
    return false;
  }
  return allowed.includes(domain);
}

export interface PresidioEmailDomainValidationResult {
  ok: boolean;
  /** Customer numbers whose approved list rejected the email. */
  rejectedCustomerNos: string[];
  emailDomain: string;
}

/**
 * Validate an email against approved domains for one or more customer accounts.
 * Only accounts with a non-empty approved list can reject the email.
 */
export function validateEmailAgainstCustomerDomains(
  email: string,
  customerNos: string[],
  domainsByCustomer: Record<string, string[] | undefined> | null | undefined
): PresidioEmailDomainValidationResult {
  const map = domainsByCustomer || {};
  const emailDomain = emailDomainOf(email);
  const rejectedCustomerNos: string[] = [];
  for (const customerNo of customerNos) {
    if (!customerNo) {
      continue;
    }
    if (!isEmailAllowedForApprovedDomains(email, map[customerNo])) {
      rejectedCustomerNos.push(customerNo);
    }
  }
  return {
    ok: rejectedCustomerNos.length === 0,
    rejectedCustomerNos,
    emailDomain,
  };
}
